import { useEffect, useMemo, useRef, useState } from "react";
import * as projectService from "../../services/projectService";
import type { Project } from "../../types/api";
import { Input } from "./input";
import { cn } from "../../lib/utils";

interface SelectRemoteProps {
  value?: string;
  onSelect: (project: { symbol: string; name: string }) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Composant de sélection avec recherche pour charger les projets depuis l'API.
 * Affiche une liste déroulante filtrée par symbole ou nom de projet.
 */
export const SelectRemote = ({
  value = "",
  onSelect,
  placeholder = "Rechercher un projet…",
  disabled = false,
  className,
}: SelectRemoteProps) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Charger les projets au montage
  useEffect(() => {
    const fetchProjects = async () => {
      setIsLoading(true);
      try {
        const data = await projectService.listProjects();
        setProjects(data);
      } catch (err) {
        console.error("Erreur lors du chargement des projets:", err);
      } finally {
        setIsLoading(false);
      }
    };
    void fetchProjects();
  }, []);

  // Filtrer les projets selon le terme de recherche
  const filteredProjects = useMemo(() => {
    if (!searchTerm.trim()) {
      return projects.slice(0, 10); // Limiter à 10 résultats par défaut
    }
    const term = searchTerm.toLowerCase();
    return projects
      .filter(
        (project) =>
          project.symbol.toLowerCase().includes(term) ||
          project.name.toLowerCase().includes(term)
      )
      .slice(0, 10);
  }, [projects, searchTerm]);

  // Fermer la liste quand on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (project: Project) => {
    onSelect({ symbol: project.symbol, name: project.name });
    setSearchTerm("");
    setIsOpen(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const selectedProject = projects.find((p) => p.symbol === value);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <Input
        ref={inputRef}
        type="text"
        value={searchTerm || (selectedProject ? selectedProject.symbol : "")}
        onChange={(event) => {
          setSearchTerm(event.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        disabled={disabled || isLoading}
        className="w-full"
      />
      {isOpen && filteredProjects.length > 0 && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-white/10 bg-black/90 shadow-lg backdrop-blur-sm">
          <ul className="py-1">
            {filteredProjects.map((project) => (
              <li
                key={project.id}
                onClick={() => handleSelect(project)}
                className={cn(
                  "cursor-pointer px-4 py-2 text-sm text-white/80 transition-colors hover:bg-white/10",
                  value === project.symbol && "bg-white/10"
                )}>
                <div className="flex items-center justify-between">
                  <span className="font-medium">{project.symbol}</span>
                  <span className="text-xs text-white/60">{project.name}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {isOpen && !isLoading && filteredProjects.length === 0 && searchTerm && (
        <div className="absolute z-50 mt-1 w-full rounded-xl border border-white/10 bg-black/90 px-4 py-2 text-sm text-white/60">
          Aucun projet trouvé
        </div>
      )}
    </div>
  );
};
